// Shared realtime bus for `public.notifications`.
//
// WHY: previously 11+ hooks each opened their own supabase.channel() with a
// postgres_changes filter on notifications for the same user. Realtime
// silently drops events once a client has too many concurrent
// postgres_changes bindings on the same table+filter, which is why the
// browser's toast/chime/badge fired inconsistently while server-side push
// (which does not use realtime) always worked.
//
// This module keeps exactly ONE channel per userId and multiplexes
// INSERT/UPDATE events to any number of in-process listeners.
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

type Row = Record<string, any>;
export type NotificationHandler = (row: Row) => void;

export type ReconnectHandler = () => void;

interface UserBus {
  channel: RealtimeChannel;
  inserts: Set<NotificationHandler>;
  updates: Set<NotificationHandler>;
  reconnects: Set<ReconnectHandler>;
}

const buses = new Map<string, UserBus>();

function ensureBus(userId: string): UserBus {
  const existing = buses.get(userId);
  if (existing) return existing;

  const inserts = new Set<NotificationHandler>();
  const updates = new Set<NotificationHandler>();
  const reconnects = new Set<ReconnectHandler>();

  const channel = supabase
    .channel(`notifications-bus-${userId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `recipient_user_id=eq.${userId}`,
      },
      (payload) => {
        const row = payload.new as Row;
        // Diagnostic: leave on — cheap, invaluable when alerts break again.
        console.debug("[notif-rt] insert", row?.id, row?.type);
        inserts.forEach((h) => {
          try { h(row); } catch (e) { console.warn("[notif-rt] insert handler threw", e); }
        });
      }
    )
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "notifications",
        filter: `recipient_user_id=eq.${userId}`,
      },
      (payload) => {
        const row = payload.new as Row;
        updates.forEach((h) => {
          try { h(row); } catch (e) { console.warn("[notif-rt] update handler threw", e); }
        });
      }
    )
    .subscribe((status) => {
      console.debug("[notif-rt] channel status", userId, status);
      // Self-heal: notify subscribers on every (re)subscribe so they can
      // refetch state that may have changed during a CHANNEL_ERROR gap.
      if (status === "SUBSCRIBED") {
        reconnects.forEach((h) => {
          try { h(); } catch (e) { console.warn("[notif-rt] reconnect handler threw", e); }
        });
      }
    });

  const bus: UserBus = { channel, inserts, updates, reconnects };
  buses.set(userId, bus);
  return bus;
}

function maybeCloseBus(userId: string) {
  const bus = buses.get(userId);
  if (!bus) return;
  if (bus.inserts.size === 0 && bus.updates.size === 0 && bus.reconnects.size === 0) {
    supabase.removeChannel(bus.channel);
    buses.delete(userId);
  }
}

/** Subscribe to notification INSERTs for a user. Returns an unsubscribe. */
export function subscribeNotificationInsert(
  userId: string,
  handler: NotificationHandler
): () => void {
  const bus = ensureBus(userId);
  bus.inserts.add(handler);
  return () => {
    bus.inserts.delete(handler);
    maybeCloseBus(userId);
  };
}

/** Subscribe to notification UPDATEs for a user. Returns an unsubscribe. */
export function subscribeNotificationUpdate(
  userId: string,
  handler: NotificationHandler
): () => void {
  const bus = ensureBus(userId);
  bus.updates.add(handler);
  return () => {
    bus.updates.delete(handler);
    maybeCloseBus(userId);
  };
}

/**
 * Subscribe to channel (re)connect events. Handler fires every time the
 * shared notifications channel reaches SUBSCRIBED — use it to refetch
 * counts/lists that may have missed events during a disconnect gap.
 */
export function subscribeNotificationReconnect(
  userId: string,
  handler: ReconnectHandler
): () => void {
  const bus = ensureBus(userId);
  bus.reconnects.add(handler);
  return () => {
    bus.reconnects.delete(handler);
    maybeCloseBus(userId);
  };
}
